import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { OFFICE_MOCKUP_URL } from "@/lib/brand";

export default function About() {
  const { t, dict } = useLang();
  const values = dict.about.values;

  const valuesAndMission = (
    <>
      <div className="grid sm:grid-cols-2 gap-4">
        {values.map((v, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 + i * 0.08 }}
            data-testid={`value-${i}`}
            className="group bg-[#0A192F] p-6 rounded-xl border border-[#0A192F] hover:border-[#3B82F6]/70 hover:shadow-[0_0_30px_rgba(59,130,246,0.25)] transition-all duration-300 cursor-default"
          >
            <div className="font-heading text-lg font-semibold text-white group-hover:text-[#93C5FD] transition-colors">{v.title}</div>
            <div className="mt-2 text-sm text-slate-300 leading-relaxed">{v.text}</div>
          </motion.div>
        ))}
      </div>

      <div className="mt-6 p-6 rounded-xl border border-[#0A192F] bg-[#0A192F] border-l-2 border-l-[#C9A96E] hover:border-[#3B82F6]/70 hover:shadow-[0_0_30px_rgba(59,130,246,0.25)] transition-all duration-300 group">
        <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C9A96E]">{t("about.missionLabel")}</div>
        <p className="mt-3 font-heading text-xl text-white group-hover:text-[#93C5FD] leading-snug transition-colors">{t("about.mission")}</p>
      </div>
    </>
  );

  return (
    <section id="about" data-testid="about-section" className="relative py-14 lg:py-20 bg-[#F3F4F6]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Desktop-only centered top header */}
        <div className="hidden lg:block text-center max-w-4xl mx-auto mb-12">
          <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]">
            {t("about.eyebrow")}
          </span>
          <h2 className="mt-6 font-heading font-bold text-[#0A192F] text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1] whitespace-pre-line">
            {t("about.title")}
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 hidden lg:block"
          >
            {valuesAndMission}
          </motion.div>

          <div className="lg:col-span-6 lg:col-start-7">
            <div className="text-center lg:hidden">
              <span className="text-sm uppercase tracking-[0.25em] font-extrabold text-[#1E3A8A]" data-testid="about-eyebrow">
                {t("about.eyebrow")}
              </span>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="mt-6 font-heading font-bold text-[#0A192F] text-3xl sm:text-4xl tracking-tight leading-[1.1] whitespace-pre-line"
              >
                {t("about.title")}
              </motion.h2>
            </div>
            <p className="mt-8 lg:mt-0 text-lg text-slate-600 leading-relaxed whitespace-pre-line text-center lg:text-left">{t("about.body")}</p>

            {/* Mobile/Tablet only: values + mission below the text */}
            <div className="mt-10 lg:hidden">
              {valuesAndMission}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
